import dayjs, { Dayjs } from 'dayjs'
import type {
  TeamPaymentCutoff,
  TeamPaymentCutoffSource,
  TeamPaymentDate,
  TeamPaymentStatus,
  TeamPaymentStatusCode,
} from '../../../services/teamBalance'

/**
 * SN-8310 (Jeff and James, Sep 22) — where a team stands against the league's
 * payment dates, as ONE pure model for the Team Budget header.
 *
 * It replaces the Season Runway's `seasonRunway()`, and fixes the three things
 * that model got wrong:
 *
 *  - THE VERDICT IS THE SERVER'S. The tier and the deadline in play come from
 *    the status `code`, never from a comparison made here. The runway decided
 *    "overdue" from its own day count, so a team that missed its minimum read
 *    "$650.00 overdue" beside a "Not Qualified" pill asking for $150.
 *  - COUNTDOWNS ROUND UP, from the cutoff INSTANT. Registration closes at 11:59
 *    PM on its last day, so on Tue Sep 22 a Sat Sep 26 close leaves five paying
 *    days, not the four a calendar-day difference gives. Jeff counted five.
 *  - LABELS USE `on`. The day a cutoff falls on in the competition's own zone,
 *    exactly as `formatCutoff` reads it, so no viewer sees a date one day off.
 *
 * Money is not in here: the header reads it from the server's KPIs
 * (`headerMoney`), and this model only says WHEN.
 */

/**
 * How the team stands, from the status code alone.
 *
 * `qualified_balance_due` is GOOD (Inon, Sep 22 — reversing the same day's amber
 * pick): once the minimum is in, the team's spot is secured, and the banner says
 * so in green. What is still owed stays in the sentence and on the milestones;
 * red is kept for the two ways a team can actually fall behind.
 */
export type StandingTier = 'good' | 'due' | 'past_due'

export const STANDING_TIER: Record<TeamPaymentStatusCode, StandingTier> = {
  minimum_payment_due: 'due',
  qualified_balance_due: 'good',
  not_qualified: 'past_due',
  balance_overdue: 'past_due',
  paid_in_full: 'good',
}

/**
 * The three dates the header marks. Inon, Sep 24: they are ALWAYS three marks —
 * "Registration Opens, Registration Ends, then season starts". Marks used to
 * merge by day, which folded a league that closes registration on the day its
 * season starts into one "Minimum payment · Balance due" mark.
 */
export type StandingMilestoneKind =
  | 'registration_opens'
  | 'registration_ends'
  | 'competition_starts'

/**
 * A payment that falls due on a date. The minimum is due when registration
 * ends. The full balance is due at the final cutoff: the season start, or
 * registration end when registration outlasts it (`final.source`).
 */
export type StandingObligation = 'minimum' | 'balance'

/**
 * `passed` is a date behind Today with nothing owed on it. Time passing is
 * neither good nor bad, so it is never the green of money that is in (Inon,
 * Sep 24: a green check beside a red "Not Qualified" told two stories).
 */
export type StandingMilestoneState =
  | 'done'
  | 'current'
  | 'upcoming'
  | 'missed'
  | 'passed'

export interface StandingMilestone {
  kind: StandingMilestoneKind
  /** The payments due on this date, minimum first. Empty for a date alone. */
  obligations: StandingObligation[]
  /** The calendar day to print (`on`, falling back to the `at` instant). */
  day: string
  /**
   * When it bites, never before the mark ahead of it (see `teamStanding`).
   * Orders the marks and places Today between them.
   */
  at: Dayjs
  /** Today is at or past it, so its title reads in the past tense. */
  passed: boolean
  state: StandingMilestoneState
  /** Position on the track, 0–100. */
  position: number
}

/** The date the rep has to hit next, or the one they missed. */
export interface StandingDeadline {
  kind: 'minimum' | 'balance'
  day: string
  at: Dayjs
  source: TeamPaymentCutoffSource
  /**
   * Whole days left, ROUNDED UP from the cutoff instant, so 1 means today is the
   * last day to pay. Null once the deadline has passed.
   */
  daysLeft: number | null
  /** Whole days since the deadline passed, 0 on the day it did. Null before. */
  daysOverdue: number | null
}

export interface TeamStanding {
  code: TeamPaymentStatusCode
  tier: StandingTier
  thresholdMet: boolean
  qualification: { day: string; at: Dayjs }
  final: { day: string; at: Dayjs; source: TeamPaymentCutoffSource }
  /** How the minimum stands, on whichever mark carries it. */
  minimumState: StandingMilestoneState
  /** How the full balance stands, on whichever mark carries it. */
  balanceState: StandingMilestoneState
  deadline: StandingDeadline | null
  milestones: StandingMilestone[]
  /** Where Today sits on the same track as the milestones, 0–100. */
  todayPosition: number
}

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * The share of the track the milestones use once Today has passed the last one:
 * the rest is the "season underway" tail Today sits in.
 */
const TRACK_WITH_TAIL = 90

/** A parsed date: the day to print and the instant it bites. */
const toPoint = (
  date: TeamPaymentDate | null | undefined
): { day: string; at: Dayjs } | null => {
  if (!date?.at) {
    return null
  }
  const at = dayjs(date.at)
  if (!at.isValid()) {
    return null
  }
  const day =
    date.on && dayjs(date.on).isValid() ? date.on : at.format('YYYY-MM-DD')

  return { day, at }
}

/** Marks that share a day read in this order, whatever time each bites at. */
const KIND_ORDER: StandingMilestoneKind[] = [
  'registration_opens',
  'registration_ends',
  'competition_starts',
]

const STATE_PRIORITY: StandingMilestoneState[] = [
  'missed',
  'current',
  'upcoming',
  'done',
  'passed',
]

/** A date that carries both payments shows the one that needs attention most. */
const mergeState = (
  a: StandingMilestoneState,
  b: StandingMilestoneState
): StandingMilestoneState =>
  STATE_PRIORITY.indexOf(a) <= STATE_PRIORITY.indexOf(b) ? a : b

const minimumStateFor = (
  code: TeamPaymentStatusCode,
  thresholdMet: boolean
): StandingMilestoneState => {
  if (thresholdMet) {
    return 'done'
  }

  return code === 'not_qualified' ? 'missed' : 'current'
}

/**
 * Not Qualified is the server's verdict on the MINIMUM. Once the final cutoff
 * has gone by as well, the balance is past due too: paying the minimum then
 * lands the team straight in Balance Overdue. So the balance reads missed
 * (Inon, Sep 24) instead of an empty dot on a date already behind Today. The
 * pill and the banner's verdict stay the server's.
 */
const balanceStateFor = (
  code: TeamPaymentStatusCode,
  finalPassed: boolean
): StandingMilestoneState => {
  switch (code) {
    case 'paid_in_full':
      return 'done'
    case 'balance_overdue':
      return 'missed'
    case 'qualified_balance_due':
      return 'current'
    case 'not_qualified':
      return finalPassed ? 'missed' : 'upcoming'
    default:
      return 'upcoming'
  }
}

const deadlineFor = (
  code: TeamPaymentStatusCode,
  qualification: { day: string; at: Dayjs },
  final: { day: string; at: Dayjs; source: TeamPaymentCutoffSource },
  qualificationSource: TeamPaymentCutoffSource,
  now: Dayjs
): StandingDeadline | null => {
  if (code === 'paid_in_full') {
    return null
  }
  const isMinimum = code === 'minimum_payment_due' || code === 'not_qualified'
  const point = isMinimum ? qualification : final
  const stillDue =
    code === 'minimum_payment_due' || code === 'qualified_balance_due'
  const msLeft = point.at.diff(now)

  return {
    kind: isMinimum ? 'minimum' : 'balance',
    day: point.day,
    at: point.at,
    source: isMinimum ? qualificationSource : final.source,
    // The server says it is still due, so it is: a clock a minute past the
    // cutoff is not a reason to print "0 days left" beside a status that has not
    // flipped. Rounded up, 1 is the last day.
    daysLeft: stillDue ? Math.max(1, Math.ceil(msLeft / DAY_MS)) : null,
    daysOverdue: stillDue ? null : Math.max(0, Math.floor(-msLeft / DAY_MS)),
  }
}

/**
 * Positions for `count` marks spread evenly, leaving the tail free when Today
 * is already past the last one.
 */
const evenPositions = (count: number, span: number): number[] =>
  count <= 1
    ? [0]
    : Array.from({ length: count }, (_, i) => (i / (count - 1)) * span)

/**
 * Today on the same track: proportional in time between its two neighbours.
 * Past the last mark it parks halfway into the tail and stays there.
 */
const todayOnTrack = (
  marks: { at: Dayjs }[],
  positions: number[],
  now: Dayjs
): number => {
  if (marks.length === 0) {
    return 0
  }
  const last = marks.length - 1
  if (!now.isAfter(marks[0].at)) {
    return 0
  }
  if (now.isAfter(marks[last].at)) {
    // Halfway into the tail: "after the last date", nothing more precise.
    return marks.length === 1 ? 50 : (positions[last] + 100) / 2
  }
  for (let i = 0; i < last; i++) {
    const from = marks[i].at
    const to = marks[i + 1].at
    if (!now.isAfter(to)) {
      const span = to.diff(from)
      const share = span > 0 ? now.diff(from) / span : 1

      return positions[i] + share * (positions[i + 1] - positions[i])
    }
  }

  return positions[last]
}

/**
 * The team's standing, or null when there is nothing to stand against.
 *
 * Null exactly when the server sends no status (it cannot resolve a threshold,
 * a registration end or a competition start — D10), sends a code this build
 * does not know, or sends cutoffs that do not parse. The header then falls back
 * to plain money figures rather than inventing a date.
 *
 * `now` is injectable so one render uses one instant, and so tests do not
 * depend on the clock.
 */
export const teamStanding = (
  status: TeamPaymentStatus | null | undefined,
  now: Dayjs = dayjs()
): TeamStanding | null => {
  if (!status) {
    return null
  }
  const code = status.code as TeamPaymentStatusCode
  const tier = STANDING_TIER[code]
  if (!tier) {
    return null
  }

  const qualificationCutoff: TeamPaymentCutoff | undefined =
    status.cutoffs?.qualification
  const finalCutoff: TeamPaymentCutoff | undefined = status.cutoffs?.final
  const qualification = toPoint(qualificationCutoff)
  const finalPoint = toPoint(finalCutoff)
  if (!qualification || !finalPoint || !finalCutoff) {
    return null
  }
  const final = { ...finalPoint, source: finalCutoff.source }
  const thresholdMet = status.threshold_met === true

  const opens = toPoint(status.registration_opens)
  const seasonStart =
    toPoint(status.competition_starts) ??
    (final.source === 'competition_start' ? final : null)
  // The final cutoff is max(registration end, season start); `source` says which.
  const balanceAtSeasonStart =
    seasonStart !== null && final.source === 'competition_start'

  const marks: Omit<StandingMilestone, 'passed' | 'state' | 'position'>[] = []
  if (opens) {
    marks.push({ kind: 'registration_opens', obligations: [], ...opens })
  }
  marks.push({
    kind: 'registration_ends',
    obligations: balanceAtSeasonStart ? ['minimum'] : ['minimum', 'balance'],
    ...qualification,
  })
  if (seasonStart) {
    marks.push({
      kind: 'competition_starts',
      obligations: balanceAtSeasonStart ? ['balance'] : [],
      day: seasonStart.day,
      at: seasonStart.at,
    })
  }

  // In date order by the competition's own calendar day; a shared day reads
  // in KIND_ORDER.
  const ordered = [...marks].sort((a, b) => {
    if (a.day === b.day) {
      return KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind)
    }

    return a.day < b.day ? -1 : 1
  })
  // A mark never bites before the one ahead of it. Registration ends at 11:59
  // PM and a season starts at midnight, so on a day that does both, the season
  // mark stays ahead of Today until registration has closed. It does not sit
  // behind Today from 12:00 AM while registration ends is still to come.
  const timed: typeof ordered = []
  ordered.forEach((mark) => {
    const previous = timed[timed.length - 1]
    timed.push(
      previous && previous.at.isAfter(mark.at)
        ? { ...mark, at: previous.at }
        : mark
    )
  })

  const finalPassed = now.isAfter(final.at)
  const minimumState = minimumStateFor(code, thresholdMet)
  const balanceState = balanceStateFor(code, finalPassed)
  const pastLast = now.isAfter(timed[timed.length - 1].at)
  const positions = evenPositions(
    timed.length,
    pastLast ? TRACK_WITH_TAIL : 100
  )
  const milestones: StandingMilestone[] = timed.map((mark, i) => {
    const passed = !now.isBefore(mark.at)
    const owed = mark.obligations.map((obligation) =>
      obligation === 'minimum' ? minimumState : balanceState
    )
    const dateOnly: StandingMilestoneState = passed ? 'passed' : 'upcoming'

    return {
      ...mark,
      passed,
      state: owed.length > 0 ? owed.reduce(mergeState) : dateOnly,
      position: positions[i],
    }
  })

  return {
    code,
    tier,
    thresholdMet,
    qualification,
    final,
    minimumState,
    balanceState,
    deadline: deadlineFor(
      code,
      qualification,
      final,
      qualificationCutoff?.source ?? 'registration_end',
      now
    ),
    milestones,
    todayPosition: todayOnTrack(timed, positions, now),
  }
}
